'use client';

import React from 'react';
import {
  CheckCircle,
  MessageSquare,
  AlertTriangle,
} from 'lucide-react';

interface ConsentManagementProps {
  consentData: {
    requestMessage: string;
    autoAddToFirstMessage: boolean;
  };
  compact?: boolean;
}

const ConsentManagement: React.FC<ConsentManagementProps> = ({
  consentData,
  compact = false
}) => {
  const content = (
    <>
      {/* TCPA Compliance Active indicator */}
      <div className="flex items-center gap-2.5 mb-5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
        </span>
        <span className="text-sm font-medium text-text-primary">TCPA Compliance Active</span>
      </div>

      {/* Current Consent Message */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <MessageSquare className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-primary">
              Current Consent Request Message
            </h3>
            <p className="text-xs text-primary/70 mt-1">
              Automatically added to first messages with new contacts
            </p>
            <div className="mt-3 p-3 bg-card-bg border border-border rounded-lg">
              <p className="text-sm text-text-primary leading-relaxed">
                &ldquo;{consentData.requestMessage}&rdquo;
              </p>
            </div>
            {/* STOP/HELP compliance hint */}
            {consentData.requestMessage && (
              <div className="mt-2">
                {!consentData.requestMessage.toUpperCase().includes('STOP') || !consentData.requestMessage.toUpperCase().includes('HELP') ? (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    <span>TCPA compliance requires STOP and HELP instructions in consent messages.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                    <CheckCircle className="h-3 w-3 shrink-0" />
                    <span>Includes required STOP and HELP language.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (compact) {
    return (
      <div className="border-t border-border pt-6">
        {content}
      </div>
    );
  }

  return (
    <div className="bg-card-bg border border-border rounded-xl p-6">
      {content}
    </div>
  );
};

export default ConsentManagement;
